import React, { useRef, useState } from 'react'
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Link, Paper, Stack, TextField, Typography,
} from '@mui/material'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import { useSnackbar } from 'notistack'
import { createPiiRequest, extractPiiError } from '../api'
import { MAX_AWB_COUNT, MAX_FILE_SIZE_MB, REASON_MAX, REASON_MIN, SAMPLE_CSV } from '../constants'

/** Data rows in the picked CSV (excludes header/blank lines) — preview only; server re-validates. */
const countCsvRows = (text) =>
  Math.max(0, text.split(/\r?\n/).filter((l) => l.trim().length > 0).length - 1)

/**
 * Screen 1 — New Request (BRD §7.2): drag-drop/browse CSV, live row count, reason with
 * counter, audited-action confirmation modal, inline API validation errors.
 */
export default function NewRequestTab({ onCreated }) {
  const { enqueueSnackbar } = useSnackbar()
  const inputRef = useRef(null)
  const [file, setFile] = useState(null)
  const [rowCount, setRowCount] = useState(null)
  const [reason, setReason] = useState('')
  const [fileError, setFileError] = useState(null)
  const [apiError, setApiError] = useState(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [dragOver, setDragOver] = useState(false)

  const pickFile = (f) => {
    setApiError(null)
    setRowCount(null)
    if (!f) return
    if (!f.name.toLowerCase().endsWith('.csv')) {
      setFile(null)
      setFileError('Only .csv files are allowed')
      return
    }
    if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setFile(null)
      setFileError(`File exceeds ${MAX_FILE_SIZE_MB} MB`)
      return
    }
    setFileError(null)
    setFile(f)
    const reader = new FileReader()
    reader.onload = () => setRowCount(countCsvRows(String(reader.result || '')))
    reader.readAsText(f)
  }

  const reasonTrimmed = reason.trim()
  const reasonValid = reasonTrimmed.length >= REASON_MIN && reasonTrimmed.length <= REASON_MAX
  const canSubmit = !!file && !fileError && reasonValid && !submitting

  const submit = () => {
    setConfirmOpen(false)
    setSubmitting(true)
    setApiError(null)
    createPiiRequest(file, reasonTrimmed)
      .then((res) => {
        enqueueSnackbar(`Request ${res.data?.requestId} submitted for L1 approval`, { variant: 'success' })
        setFile(null)
        setRowCount(null)
        setReason('')
        onCreated && onCreated()
      })
      .catch((err) => setApiError(extractPiiError(err, 'Failed to create the request')))
      .finally(() => setSubmitting(false))
  }

  const downloadSample = () => {
    const url = URL.createObjectURL(new Blob([SAMPLE_CSV], { type: 'text/csv' }))
    const a = document.createElement('a')
    a.href = url
    a.download = 'pii_request_sample.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Paper
        variant="outlined"
        onClick={() => inputRef.current && inputRef.current.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragOver(false)
          pickFile(e.dataTransfer.files && e.dataTransfer.files[0])
        }}
        sx={{
          p: 4, textAlign: 'center', cursor: 'pointer', borderStyle: 'dashed',
          bgcolor: dragOver ? 'action.hover' : 'background.paper',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv"
          hidden
          onChange={(e) => { pickFile(e.target.files && e.target.files[0]); e.target.value = '' }}
        />
        <UploadFileOutlinedIcon color="action" sx={{ fontSize: 40 }} />
        <Typography sx={{ mt: 1 }}>
          {file ? file.name : 'Drag & drop the AWB CSV here, or click to browse'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Single column with header <b>awb_number</b> · max {MAX_AWB_COUNT.toLocaleString()} AWBs · ≤ {MAX_FILE_SIZE_MB} MB
        </Typography>
        {rowCount != null && (
          <Typography variant="body2" sx={{ mt: 1 }} color={rowCount > MAX_AWB_COUNT ? 'error' : 'text.primary'}>
            {rowCount.toLocaleString()} data row{rowCount === 1 ? '' : 's'} detected
            {rowCount > MAX_AWB_COUNT ? ` — exceeds the ${MAX_AWB_COUNT.toLocaleString()} limit` : ''}
          </Typography>
        )}
      </Paper>
      {fileError && <Alert severity="error" sx={{ mt: 1 }}>{fileError}</Alert>}

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.5 }}>
        <Link component="button" variant="caption" onClick={downloadSample}>Download sample CSV</Link>
      </Stack>

      <TextField
        label="Reason for requesting PII"
        placeholder="e.g. Legal escalation follow-up for case #1234 — customer contact required"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        multiline
        minRows={3}
        fullWidth
        sx={{ mt: 2 }}
        error={reason.length > 0 && !reasonValid}
        helperText={`${reasonTrimmed.length}/${REASON_MAX} characters (minimum ${REASON_MIN})`}
      />

      {apiError && <Alert severity="error" sx={{ mt: 2 }}>{apiError}</Alert>}

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button variant="contained" disabled={!canSubmit} onClick={() => setConfirmOpen(true)}>
          Submit for approval
        </Button>
      </Stack>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm PII data request</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are requesting customer PII for {rowCount != null ? rowCount.toLocaleString() : 'the uploaded'} AWBs.
            This action is audited. Proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submit}>Yes, submit</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
